import type { User } from "@/types";

export const BREAK_GLASS_HEADER = "X-Break-Glass";
export const BREAK_GLASS_SESSION_HEADER = "X-Break-Glass-Session";
export const BREAK_GLASS_STORAGE_KEY = "ehr_break_glass_session";
export const BREAK_GLASS_TTL_MS = 60 * 60 * 1000; // 1 hour
export const BREAK_GLASS_MIN_REASON_LENGTH = 10;
export const PLAC_DENIED_EVENT = "ehr:plac-denied";

export interface BreakGlassSession {
  sessionId: string;
  reason: string;
  activatedAt: number;
  expiresAt: number;
}

export interface PlacDeniedDetail {
  message: string;
  url?: string;
}

export function canUseBreakGlass(user: User | null | undefined): boolean {
  if (!user?.break_glass_enabled) return false;
  if (user.role === "super_admin" || user.role === "org_admin") return true;
  const perms = user.permissions ?? [];
  return perms.includes("settings:override") || perms.includes("*");
}

export function readBreakGlassSession(): BreakGlassSession | null {
  try {
    const raw = sessionStorage.getItem(BREAK_GLASS_STORAGE_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as BreakGlassSession;
    if (!session.sessionId || !session.reason || !session.expiresAt) {
      sessionStorage.removeItem(BREAK_GLASS_STORAGE_KEY);
      return null;
    }
    if (Date.now() >= session.expiresAt) {
      sessionStorage.removeItem(BREAK_GLASS_STORAGE_KEY);
      return null;
    }
    return session;
  } catch {
    sessionStorage.removeItem(BREAK_GLASS_STORAGE_KEY);
    return null;
  }
}

export function writeBreakGlassSession(reason: string): BreakGlassSession {
  const now = Date.now();
  const session: BreakGlassSession = {
    sessionId: crypto.randomUUID(),
    reason: reason.trim(),
    activatedAt: now,
    expiresAt: now + BREAK_GLASS_TTL_MS,
  };
  sessionStorage.setItem(BREAK_GLASS_STORAGE_KEY, JSON.stringify(session));
  return session;
}

export function clearBreakGlassSession(): void {
  sessionStorage.removeItem(BREAK_GLASS_STORAGE_KEY);
}

export function getBreakGlassHeaders(): Record<string, string> {
  const session = readBreakGlassSession();
  if (!session) return {};
  return {
    [BREAK_GLASS_HEADER]: session.reason,
    [BREAK_GLASS_SESSION_HEADER]: session.sessionId,
  };
}

export function isPlacDeniedMessage(message: string | undefined): boolean {
  if (!message) return false;
  const lower = message.toLowerCase();
  return (
    lower.includes("patient-restricted") ||
    lower.includes("this patient record") ||
    lower.includes("explicit patient scope") ||
    lower.includes("patient scope")
  );
}

export function dispatchPlacDenied(detail: PlacDeniedDetail): void {
  window.dispatchEvent(new CustomEvent<PlacDeniedDetail>(PLAC_DENIED_EVENT, { detail }));
}
